/**
 * CalculatorDisplay — Human-readable calculator config display.
 *
 * Renders the formatted calculator output in two modes:
 * - "compact": short summary for list views (AC-7)
 * - "full": full display with optional table and collapsible JSON (AC-6, AC-7)
 *
 * @see AC-15 in docs/specifications/2026-03-24-pricing-calculator-extraction.md
 * @see AC-1 through AC-7 in docs/specifications/2026-03-22-billing-admin-pricing-preview.md
 */
import React from "react";
import {
  formatCalculatorConfig,
  type CalculatorDisplayResult,
} from "./formatCalculatorConfig";
import type { PricingCalculator } from "../../../../types/adminBilling";

export interface CalculatorDisplayProps {
  calculator: PricingCalculator;
  /** Display mode: "compact" for list rows, "full" for detail modals */
  mode?: "compact" | "full";
}

/**
 * Renders human-readable calculator configuration.
 *
 * - FIXED_AMOUNT: "49,00 PLN netto"
 * - PERCENTAGE: "23%"
 * - FREE: "Bezpłatny"
 * - DISCRETE_POINTS: table of key -> amount
 * - DEGRESSIVE_EXPONENTIAL: formula with parameters
 * - Unknown: type name + collapsible raw JSON
 */
export const CalculatorDisplay: React.FC<CalculatorDisplayProps> = ({
  calculator,
  mode = "full",
}) => {
  const result: CalculatorDisplayResult = formatCalculatorConfig(
    calculator.type,
    calculator.config
  );

  return (
    <div>
      {/* Human-readable summary */}
      {result.type === "table" && mode === "full" && result.tableRows ? (
        <div className="space-y-1">
          <table className="text-sm" aria-label="Punkty cenowe">
            <thead>
              <tr>
                <th className="pr-4 text-left text-xs font-medium text-neutral-500">Okres</th>
                <th></th>
                <th className="pl-2 text-left text-xs font-medium text-neutral-500">Kwota netto</th>
              </tr>
            </thead>
            <tbody>
              {result.tableRows.map((row) => (
                <tr key={row.key}>
                  <td className="pr-4 text-neutral-600 font-mono">
                    {row.key}
                  </td>
                  <td className="text-neutral-500">&rarr;</td>
                  <td className="pl-2 text-neutral-800 font-medium">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="text-sm text-neutral-800">{result.summary}</span>
      )}

      {/* AC-6: Collapsible raw JSON (default collapsed) */}
      {mode === "full" && (
        <details className="mt-2">
          <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-700">
            Pokaż JSON
          </summary>
          <pre className="text-xs mt-1 bg-white border border-neutral-200 rounded p-2 overflow-x-auto">
            {result.rawJson}
          </pre>
        </details>
      )}
    </div>
  );
};
